/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Applications queue in UI v2 (module RE_CUSTOMER_PORTAL, object
 * re_applications): what buyers and renters sent from the agency's website,
 * newest first, with the open work selected by default.
 *
 * GET /re/applications (status, type, search, page) returns the page and the
 * counts by status for the filter chips. Record scope applies on the server,
 * so an agent sees the applications they may see, as on every RE list.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { Inbox } from "lucide-react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageHeader from "components/v2/PageHeader";
import SegmentedControl from "components/v2/SegmentedControl";
import { FilterBar, FilterChip, FilterSearch } from "components/v2/filters";
import useDebounce from "hooks/useDebounce";
import { v2 as t } from "styles/tokens/v2";
import { errorMessage, formatDate, formatNumber, useReGet } from "../../screens/api";
import useReVariant from "../../useReVariant";
import { isAustralia } from "../../variant";
import { Avatar, Pill, RE_ACCENT } from "../parts";
import { QUEUE_FILTERS, STATUS_LABEL, STATUS_TONE, filterCount, typeLabel } from "./logic";

const PAGE_SIZE = 25;

export function listingName(listing) {
  if (!listing) return "Listing no longer published";
  return listing.title || [listing.unit_number && `Unit ${listing.unit_number}`, listing.project].filter(Boolean).join(", ") || `Listing ${listing.id}`;
}

function Row({ app, au, onOpen }) {
  return (
    <Box
      component="tr"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      sx={{ cursor: "pointer", "&:hover td, &:focus-visible td": { bgcolor: t.surface2 }, "&:focus-visible": { outline: `2px solid ${RE_ACCENT.accent}`, outlineOffset: -2 } }}
    >
      <Box component="td">
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: t.text }}>{app.applicant_name || app.applicant_email || "Applicant"}</Typography>
        {app.applicant_email && app.applicant_email !== app.applicant_name && (
          <Typography sx={{ fontSize: 13, color: t.text3 }}>{app.applicant_email}</Typography>
        )}
      </Box>
      <Box component="td">
        <Typography sx={{ fontSize: 14, color: t.text }}>{listingName(app.listing)}</Typography>
        <Typography sx={{ fontSize: 13, color: t.text3 }}>{`${typeLabel(app.type, au)} · ${app.reference || `#${app.id}`}`}</Typography>
      </Box>
      <Box component="td"><Pill tone={STATUS_TONE[app.status]}>{STATUS_LABEL[app.status] || app.status}</Pill></Box>
      <Box component="td" sx={{ display: { xs: "none", md: "table-cell" } }}>
        {app.assigned_to_name ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar name={app.assigned_to_name} />
            <Typography sx={{ fontSize: 14, color: t.text2 }}>{app.assigned_to_name}</Typography>
          </Box>
        ) : <Typography sx={{ fontSize: 14, color: t.text3 }}>Unassigned</Typography>}
      </Box>
      <Box component="td" sx={{ whiteSpace: "nowrap", color: t.text2, fontSize: 14 }}>{formatDate(app.submitted_at)}</Box>
    </Box>
  );
}

export default function ApplicationsV2() {
  const navigate = useNavigate();
  const pack = useReVariant();
  const au = isAustralia(pack);
  const [filter, setFilter] = useState(QUEUE_FILTERS[0].value);
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debounced = useDebounce(search, 350);
  const query = { status: filter || null, type: type || null, search: debounced || null, page, page_size: PAGE_SIZE, sort_by: "submitted_at", sort_order: "desc" };
  const { data, loading, error, reload } = useReGet("/re/applications", query);
  const items = data?.items || [];
  const total = data?.total || 0;
  const counts = data?.counts || {};
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const change = (setter) => (value) => { setter(value); setPage(1); };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 6 }}>
        <PageHeader
          title="Applications"
          summary={`${formatNumber(filterCount(QUEUE_FILTERS[0], counts))} open · sent by buyers and renters from your website`}
        />

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }} role="group" aria-label="Status">
          {QUEUE_FILTERS.map((f) => (
            <FilterChip
              key={f.label}
              label={f.label}
              count={formatNumber(filterCount(f, counts))}
              selected={f.value === filter}
              onClick={() => change(setFilter)(f.value)}
            />
          ))}
        </Box>

        <FilterBar count={`${formatNumber(total)} shown`} sx={{ mb: 2.5 }}>
          <SegmentedControl
            label="Type"
            value={type}
            onChange={change(setType)}
            options={[
              { value: "", label: "All" },
              { value: "PURCHASE", label: au ? "Offers" : "Purchases" },
              { value: "RENTAL", label: "Rentals" },
            ]}
          />
          <FilterSearch
            placeholder="Search name, email or reference"
            value={search}
            onChange={(v) => { setSearch(v); setPage(1); }}
            width={280}
          />
        </FilterBar>

        {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>{errorMessage(error, "Applications could not be loaded.")}</Alert>}

        <Box sx={{ bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: "10px", overflowX: "auto" }}>
          <Box
            component="table"
            sx={{
              width: "100%", borderCollapse: "collapse",
              "& th": { textAlign: "left", fontSize: 12.5, fontWeight: 600, color: t.text3, textTransform: "uppercase", letterSpacing: "0.04em", px: 2, py: 1.25, borderBottom: `1px solid ${t.border}`, bgcolor: t.surface2 },
              "& td": { px: 2, py: 1.5, borderBottom: `1px solid ${t.border}`, verticalAlign: "middle" },
              "& tr:last-of-type td": { borderBottom: 0 },
            }}
          >
            <thead>
              <tr>
                <th scope="col">Applicant</th>
                <th scope="col">Listing</th>
                <th scope="col">Status</th>
                <Box component="th" scope="col" sx={{ display: { xs: "none", md: "table-cell" } }}>Agent</Box>
                <th scope="col">Sent</th>
              </tr>
            </thead>
            <tbody>
              {loading && !items.length
                ? [0, 1, 2].map((i) => <tr key={i}><td colSpan={5}><Skeleton height={36} /></td></tr>)
                : items.map((app) => <Row key={app.id} app={app} au={au} onOpen={() => navigate(`/real-estate/applications/${app.id}`)} />)}
            </tbody>
          </Box>
          {!loading && !items.length && !error && (
            <Box sx={{ textAlign: "center", py: 6, color: t.text3 }}>
              <Inbox size={32} color={RE_ACCENT.ink} aria-hidden />
              <Typography sx={{ mt: 1, fontSize: 15, color: t.text2 }}>No applications here.</Typography>
              <Typography sx={{ fontSize: 13.5, color: t.text3 }}>Buyers and renters apply from a listing on your website.</Typography>
            </Box>
          )}
        </Box>

        {pages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, mt: 3 }}>
            <Button variant="outlined" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Typography sx={{ fontSize: 13.5, color: t.text2 }}>{`Page ${page} of ${pages}`}</Typography>
            <Button variant="outlined" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
}
